// The one place a verified auth response becomes a live session (email code + OAuth).
import { connectToServer, workspaceState } from "$lib/state/app.svelte.js";
import { isDesktopApp } from "$lib/utils.js";
import { resolvePostSignup, type PendingDomainMatch } from "$lib/post-signup-landing.js";
import type { OnboardingWorkspace } from "$lib/components/onboarding/TeamFoundCard.svelte";

export interface AuthResponse {
  token: string;
  refreshToken?: string;
  pendingDomainJoin?: PendingDomainMatch | null;
  /** Set when the signup had a pending invite that was never accepted. */
  pendingInviteToken?: string | null;
}

export type AdoptOutcome =
  | { kind: "domain-match"; match: PendingDomainMatch }
  | { kind: "path"; path: string };

export function wsUrlFromBase(base: string): string {
  const protocol = base.startsWith("https") ? "wss" : "ws";
  return `${protocol}://${base.replace(/^https?:\/\//, "")}/api/ws`;
}

/** isNew: a fresh signup (first-run onboarding / domain match); false = plain sign-in. */
export async function adoptSession(
  data: AuthResponse,
  opts: { wsUrl: string; nextDest: string | null; isNew: boolean },
): Promise<AdoptOutcome> {
  await connectToServer(opts.wsUrl, data.token, data.refreshToken);
  if (!opts.isNew) return { kind: "path", path: opts.nextDest ?? "/workspace" };
  return resolvePostSignup({
    nextDest: opts.nextDest,
    pendingDomainJoin: data.pendingDomainJoin,
    workspaces: workspaceState.list,
    isDesktop: isDesktopApp(),
    pendingInviteToken: data.pendingInviteToken,
  });
}

function initialsOf(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  const first = parts[0];
  const last = parts[parts.length - 1];
  if (!first || !last) return "?";
  if (first === last) return first.slice(0, 2).toUpperCase();
  return (first.charAt(0) + last.charAt(0)).toUpperCase();
}

export function toOnboardingWorkspace(m: PendingDomainMatch): OnboardingWorkspace {
  return {
    id: m.workspaceId,
    name: m.name,
    domain: m.domain,
    memberCount: m.memberCount,
    members: m.members.map((x) => ({
      name: x.name,
      initials: initialsOf(x.name),
      avatar: x.avatarUrl ?? undefined,
    })),
    overflowCount: Math.max(0, m.memberCount - m.members.length),
  };
}
